/** Read-only relay clock evidence. Browser wall time is never compared to
 * venue time; only local elapsed durations cross this boundary. */
export interface LocalClockStamp { monotonicMs: number; wallMs: number }
export interface RelayClockBounds { lowerMs: number; upperMs: number }
export type RelayClock = () => RelayClockBounds;

export function localClockStamp(): LocalClockStamp {
  return { monotonicMs: performance.now(), wallMs: Date.now() };
}

/** A wall-clock jump forward may conservatively expire an alert. A backward
 * jump cannot extend it. The wall delta also covers OS sleep on platforms
 * whose performance clock pauses during sleep. Constant skew cancels out. */
export function elapsedSince(stamp: LocalClockStamp): number {
  return Math.max(0, performance.now() - stamp.monotonicMs, Date.now() - stamp.wallMs);
}

export function createRelayClock(serverMs: unknown, requestStarted: LocalClockStamp): RelayClock | null {
  if (typeof serverMs !== 'number' || !Number.isFinite(serverMs) || serverMs <= 0) return null;
  const received = localClockStamp();
  const roundTripMs = elapsedSince(requestStarted);
  if (!Number.isFinite(roundTripMs)) return null;
  return () => ({
    // Monotonic elapsed is a conservative lower bound even across OS sleep.
    lowerMs: serverMs + Math.max(0, performance.now() - received.monotonicMs),
    // Full RTT deliberately overestimates transport age; never freshen a
    // buffered frame merely because it has just reached the subscriber.
    upperMs: serverMs + roundTripMs + elapsedSince(received),
  });
}
